import type {
  PatientResponsibilityEntry,
  SingleCoverageInformation,
} from "../../../data-model/coverageInformation";
import {
  compareCoverageServiceTypeCodes,
  isUsableCoverageServiceType,
} from "./resolveCoverageServiceType";

/**
 * Picks a default service type code for seeding a manual BV row when EV returned
 * no treatment tabs.
 *
 * @param serviceTypeCodeLabelMap - Order/coverage service-type labels keyed by code
 * @returns First usable code when the map is non-empty, otherwise an empty string
 */
export const resolveDefaultServiceTypeCodeForSeed = (
  serviceTypeCodeLabelMap?: ReadonlyMap<string, string>,
): string => {
  if (!serviceTypeCodeLabelMap || serviceTypeCodeLabelMap.size === 0) {
    return "";
  }
  const codes = [...serviceTypeCodeLabelMap.keys()]
    .map((code) => code.trim())
    .filter((code) => isUsableCoverageServiceType(code));
  if (codes.length === 0) {
    return "";
  }
  codes.sort(compareCoverageServiceTypeCodes);
  return codes[0] ?? "";
};

/**
 * Builds an empty patient-responsibility tab entry for manual BV entry.
 *
 * @param serviceTypeCode - Postgres `service_type` for the seeded treatment tab
 * @param serviceTypeDescription - Tab label fallback when no map label exists
 * @returns One treatment-level PR row with empty financial buckets
 */
export const createEmptyPatientResponsibilityEntry = (
  serviceTypeCode: string,
  serviceTypeDescription = "Treatment",
): PatientResponsibilityEntry => ({
  singlePatientResponsibility: {
    serviceTypeInformation: {
      serviceType: serviceTypeCode,
      serviceTypeDescription,
    },
    deductibleAmountInformation: [],
    outOfPocketAmountInformation: [],
    miscellaneousAmountInformation: [],
  },
});

/**
 * Ensures a coverage working copy has at least one treatment tab when the user
 * opens edit mode with no EV patient-responsibility rows.
 *
 * Skips Medicaid tiers: they use a read-only Treatment / Additional Payer view
 * instead of BV financial editors, and seeding a PR row would make Save require
 * invisible deductible/OOP fields and emit a non-existent service_type update.
 *
 * @param draft - Mutable coverage working copy at edit start
 * @param serviceTypeCodeLabelMap - Optional code-to-label map for tab labels
 * @returns Whether a default PR row was appended
 */
export const seedEmptyPatientResponsibilityIfNeeded = (
  draft: SingleCoverageInformation,
  serviceTypeCodeLabelMap?: ReadonlyMap<string, string>,
): boolean => {
  if (draft.medicaidInformation) {
    return false;
  }
  if ((draft.patientResponsibilityInformation ?? []).length > 0) {
    return false;
  }

  const serviceTypeCode = resolveDefaultServiceTypeCodeForSeed(
    serviceTypeCodeLabelMap,
  );
  if (!isUsableCoverageServiceType(serviceTypeCode)) {
    return false;
  }

  const serviceTypeDescription =
    serviceTypeCodeLabelMap?.get(serviceTypeCode) ?? "Treatment";

  draft.patientResponsibilityInformation = [
    createEmptyPatientResponsibilityEntry(
      serviceTypeCode,
      serviceTypeDescription,
    ),
  ];
  return true;
};
